import { useEffect, useMemo, useState } from 'react';
import { Alert, Box, MenuItem, Typography } from '@mui/material';
import {
  DialogContainer,
  DialogTitle,
  DialogBody,
  DialogActions,
  CustomAutoComplete,
  CustomTextField,
} from '../../../common/components';
import { useAdjustArticleStock } from '../../../hooks/useArticleStocks';
import {
  ADJUSTMENT_TYPE_OPTIONS,
  INITIAL_ADJUST_STOCK_STATE,
} from '../constants/inventoryArticlesConstants';
import {
  buildAdjustStockPayload,
  getArticleLabel,
  getArticleStockApiErrorMessage,
  getEstimatedResultingStock,
  getWarehouseLabel,
  formatStockQuantity,
  toStockNumber,
  validateAdjustStockForm,
} from '../utils/inventoryArticlesUtils';
import { useReportPendingState } from '../../hooks/useReportPendingState';
import { guardDialogClose } from '../../utils/actionLockUtils';

const AdjustArticleStockDialog = ({
  open,
  onClose,
  article,
  stockRow = null,
  stocks = [],
  warehouses = [],
  warehousesByUuid = {},
  onSuccess,
  onPendingChange,
}) => {
  const [formData, setFormData] = useState(INITIAL_ADJUST_STOCK_STATE);
  const [errors, setErrors] = useState({});
  const [errorMessage, setErrorMessage] = useState('');
  const adjustMutation = useAdjustArticleStock();
  const isLocked = adjustMutation.isPending;

  useReportPendingState(isLocked, onPendingChange);

  const isExistingStockRow = Boolean(stockRow?.uuid_warehouse);
  const selectedWarehouseId = isExistingStockRow
    ? stockRow.uuid_warehouse
    : formData.uuid_warehouse;

  const currentStock = useMemo(() => {
    if (isExistingStockRow) {
      return toStockNumber(stockRow?.quantity_on_hand);
    }

    if (!selectedWarehouseId) {
      return 0;
    }

    const matchingStock = stocks.find(
      (stock) => stock.uuid_warehouse === selectedWarehouseId
    );

    return toStockNumber(matchingStock?.quantity_on_hand);
  }, [
    isExistingStockRow,
    selectedWarehouseId,
    stockRow?.quantity_on_hand,
    stocks,
  ]);

  const warehouseLabel = getWarehouseLabel(warehousesByUuid, selectedWarehouseId);

  const estimatedStock = useMemo(
    () => getEstimatedResultingStock(currentStock, formData.adjustmentType, formData.quantity),
    [currentStock, formData.adjustmentType, formData.quantity]
  );

  useEffect(() => {
    if (open) {
      setFormData({
        ...INITIAL_ADJUST_STOCK_STATE,
        uuid_warehouse: stockRow?.uuid_warehouse || '',
      });
      setErrors({});
      setErrorMessage('');
    }
  }, [open, stockRow?.uuid, stockRow?.uuid_warehouse]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    if (errors[name]) {
      setErrors((prev) => {
        const updated = { ...prev };
        delete updated[name];
        return updated;
      });
    }

    if (errorMessage) {
      setErrorMessage('');
    }
  };

  const handleSubmit = async () => {
    const validationErrors = validateAdjustStockForm(formData, currentStock, {
      requireWarehouse: !isExistingStockRow,
    });

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    const warehouseUuid = isExistingStockRow
      ? stockRow.uuid_warehouse
      : formData.uuid_warehouse;

    if (!article?.uuid || !warehouseUuid) {
      return;
    }

    try {
      setErrorMessage('');
      const payload = buildAdjustStockPayload(article.uuid, warehouseUuid, formData);
      await adjustMutation.mutateAsync(payload);
      onSuccess?.();
      onClose();
    } catch (error) {
      setErrorMessage(
        getArticleStockApiErrorMessage(error, 'No se pudo ajustar el stock')
      );
    }
  };

  if (!open) return null;

  return (
    <DialogContainer open={open} onClose={guardDialogClose(isLocked, onClose)} size="medium">
      <DialogTitle>Ajustar stock</DialogTitle>

      <DialogBody sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {errorMessage && <Alert severity="error">{errorMessage}</Alert>}

        <CustomTextField
          label="Artículo"
          name="article"
          value={getArticleLabel(article)}
          disabled
        />

        {isExistingStockRow ? (
          <CustomTextField
            label="Depósito"
            name="warehouse"
            value={warehouseLabel}
            disabled
          />
        ) : (
          <CustomAutoComplete
            label="Depósito"
            name="uuid_warehouse"
            value={formData.uuid_warehouse}
            onChange={handleChange}
            required
            error={Boolean(errors.uuid_warehouse)}
            helperText={errors.uuid_warehouse}
          >
            {warehouses.map((warehouse) => (
              <MenuItem key={warehouse.uuid} value={warehouse.uuid}>
                {warehouse.name || warehouse.code}
              </MenuItem>
            ))}
          </CustomAutoComplete>
        )}

        <CustomTextField
          label="Stock actual"
          name="currentStock"
          value={formatStockQuantity(currentStock)}
          disabled
        />

        <CustomAutoComplete
          label="Tipo de ajuste"
          name="adjustmentType"
          value={formData.adjustmentType}
          onChange={handleChange}
        >
          {ADJUSTMENT_TYPE_OPTIONS.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </CustomAutoComplete>

        <CustomTextField
          label="Cantidad"
          name="quantity"
          type="number"
          value={formData.quantity}
          onChange={handleChange}
          required
          error={Boolean(errors.quantity)}
          helperText={errors.quantity}
          inputProps={{ min: 0, step: 'any' }}
        />

        <CustomTextField
          label="Motivo"
          name="reason"
          value={formData.reason}
          onChange={handleChange}
          required
          multiline
          minRows={2}
          error={Boolean(errors.reason)}
          helperText={errors.reason}
        />

        <Box>
          <Typography variant="body2" color="text.secondary">
            Stock resultante estimado
          </Typography>
          <Typography variant="h6" fontWeight={700}>
            {formatStockQuantity(estimatedStock)}
          </Typography>
        </Box>
      </DialogBody>

      <DialogActions
        handleClose={onClose}
        handleSave={handleSubmit}
        saveLabel="Guardar ajuste"
        saveButtonProps={{ isLoading: isLocked }}
        cancelButtonProps={{ disabled: isLocked }}
      />
    </DialogContainer>
  );
};

export default AdjustArticleStockDialog;
